import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import YouTube from "react-youtube";

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace("https://", "wss://").replace("http://", "ws://");

export default function BroadcastDisplayPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const broadcastKey = searchParams.get("key") || "";

  const [content, setContent] = useState({ type: "black" });
  const [transition, setTransition] = useState("cut");
  const [transitioning, setTransitioning] = useState(false);
  const [connected, setConnected] = useState(false);
  const [sessionValid, setSessionValid] = useState(true);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const ytRef = useRef(null);
  const prevContentRef = useRef(null);

  // Fullscreen
  const containerRef = useRef(null);
  const enterFullscreen = () => {
    if (containerRef.current?.requestFullscreen) containerRef.current.requestFullscreen();
  };

  // Fetch initial state
  useEffect(() => {
    fetch(`${API}/api/broadcast/public/${sessionId}?key=${broadcastKey}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        if (data.session?.current_program) setContent(data.session.current_program);
      })
      .catch(() => setSessionValid(false));
  }, [sessionId, broadcastKey]);

  // WebSocket connection
  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    const ws = new WebSocket(`${WS_URL}/api/broadcast/ws/display/${sessionId}?key=${broadcastKey}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      // Send heartbeat every 20s
      const hb = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "heartbeat" }));
      }, 20000);
      ws._hb = hb;
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "program_update") {
          const newContent = msg.data || {};
          const trans = msg.transition || "cut";
          if (trans === "fade") {
            setTransitioning(true);
            setTimeout(() => {
              setContent(newContent);
              setTransitioning(false);
            }, 500);
          } else {
            setContent(newContent);
          }
        } else if (msg.type === "video_command") {
          handleVideoCommand(msg.command, msg.value);
        } else if (msg.type === "session_ended") {
          setContent({ type: "black" });
          setSessionValid(false);
        }
      } catch {}
    };

    ws.onclose = () => {
      clearInterval(ws._hb);
      setConnected(false);
      reconnectRef.current = setTimeout(connectWs, 3000);
    };

    ws.onerror = () => ws.close();
  }, [sessionId, broadcastKey]);

  useEffect(() => {
    connectWs();
    return () => {
      clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connectWs]);

  const handleVideoCommand = (command, value) => {
    const player = ytRef.current;
    if (!player) return;
    try {
      switch (command) {
        case "play": player.playVideo(); break;
        case "pause": player.pauseVideo(); break;
        case "restart": player.seekTo(0, true); player.playVideo(); break;
        case "volume": player.setVolume(value || 100); break;
        case "seek": player.seekTo(value || 0, true); break;
      }
    } catch {}
  };

  if (!sessionValid) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <p className="text-white/20 text-sm">Session terminee</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black overflow-hidden select-none"
      style={{ cursor: "none", transition: transitioning ? "opacity 0.5s ease" : "none", opacity: transitioning ? 0 : 1 }}
      onDoubleClick={enterFullscreen}
      data-testid="broadcast-display"
    >
      {/* Content Renderer */}
      {content.type === "black" && <div className="w-full h-full bg-black" />}

      {content.type === "wait" && (
        <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${content.config?.bg_color || "#0B1120"}, ${content.config?.bg_end || "#1a1a2e"})` }}>
          <div className="text-center space-y-4">
            {content.config?.logo_url && <img src={content.config.logo_url} alt="" className="h-24 mx-auto object-contain mb-4" />}
            <div className="w-16 h-16 mx-auto border-4 border-white/20 border-t-white/60 rounded-full animate-spin" />
            <p className="text-white/80 text-2xl font-light">{content.message || content.config?.message || "L'evenement va commencer dans quelques instants"}</p>
          </div>
        </div>
      )}

      {content.type === "image" && content.config?.url && (
        <div className="w-full h-full flex items-center justify-center bg-black">
          <img src={content.config.url} alt="" className="max-w-full max-h-full object-contain" draggable={false} />
        </div>
      )}

      {content.type === "pdf" && content.config?.full_url && (
        <div className="w-full h-full flex items-center justify-center bg-black">
          <img src={content.config.full_url} alt="" className="max-w-full max-h-full object-contain" draggable={false} />
        </div>
      )}

      {content.type === "youtube" && content.config?.video_id && (
        <div className="w-full h-full flex items-center justify-center bg-black">
          <YouTube
            videoId={content.config.video_id}
            opts={{
              width: "100%", height: "100%",
              playerVars: {
                autoplay: content.config.autoplay ? 1 : 0,
                controls: 0, modestbranding: 1, rel: 0, showinfo: 0,
                fs: 0, iv_load_policy: 3,
              },
            }}
            onReady={(e) => { ytRef.current = e.target; }}
            className="w-full h-full"
            iframeClassName="w-full h-full"
          />
        </div>
      )}

      {content.type === "video_url" && content.config?.url && (
        <div className="w-full h-full flex items-center justify-center bg-black">
          <video
            src={content.config.url}
            autoPlay={content.config.autoplay}
            controls={false}
            className="max-w-full max-h-full"
            style={{ objectFit: "contain" }}
          />
        </div>
      )}

      {content.type === "text" && (
        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: content.config?.bg_color || "#0B1120" }}>
          <p className="text-center px-12 max-w-4xl" style={{
            color: content.config?.color || "#ffffff",
            fontSize: `${content.config?.font_size || 48}px`,
            fontWeight: content.config?.bold ? "bold" : "normal",
            lineHeight: 1.4,
          }}>
            {content.config?.text || ""}
          </p>
        </div>
      )}

      {content.type === "qr" && (
        <div className="w-full h-full flex items-center justify-center bg-white">
          <div className="text-center space-y-6">
            {content.config?.qr_image && <img src={content.config.qr_image} alt="QR" className="w-80 h-80 mx-auto" />}
            {content.config?.title && <p className="text-black text-3xl font-bold">{content.config.title}</p>}
            {content.config?.subtitle && <p className="text-gray-600 text-xl">{content.config.subtitle}</p>}
          </div>
        </div>
      )}

      {content.type === "livestats" && <LiveStatsDisplay sessionId={sessionId} broadcastKey={broadcastKey} eventId={content.config?.event_id} />}

      {content.type === "countdown" && <CountdownDisplay config={content.config} />}

      {content.type === "local_video" && (
        <div className="w-full h-full bg-black flex items-center justify-center">
          <video
            src={`${API}${content.config?.url}`}
            className="w-full h-full object-contain"
            autoPlay
            playsInline
            data-testid="broadcast-local-video"
          />
        </div>
      )}

      {/* Connection indicator (bottom-right, very subtle, auto-hides) */}
      {!connected && (
        <div className="absolute bottom-4 right-4 bg-red-500/80 text-white text-xs px-3 py-1.5 rounded-full animate-pulse">
          Reconnexion...
        </div>
      )}
    </div>
  );
}

// Live Stats display component (polls for updates)
function LiveStatsDisplay({ sessionId, broadcastKey, eventId }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!sessionId || !broadcastKey) return;
    const fetchStats = () => {
      fetch(`${API}/api/broadcast/public-stats/${sessionId}?key=${broadcastKey}`)
        .then(r => r.json())
        .then(d => setStats(d))
        .catch(() => {});
    };
    fetchStats();
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
  }, [sessionId, broadcastKey]);

  if (!stats) return <div className="w-full h-full bg-[#0B1120] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#0099CC]/30 border-t-[#0099CC] rounded-full animate-spin" /></div>;

  const s = stats.stats || {};
  return (
    <div className="w-full h-full bg-gradient-to-br from-[#0B1120] via-[#0B1120] to-[#0d1a2d] flex flex-col items-center justify-center p-12">
      {/* Event info */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-white mb-2">{stats.event?.title}</h1>
        <p className="text-[#64748B] text-lg">{stats.organization?.name}</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 w-full max-w-5xl">
        {[
          { label: "Inscrits", value: s.inscrits || 0, color: "#0099CC" },
          { label: "Presents", value: s.presents || 0, color: "#10B981" },
          { label: "En attente", value: s.attente || 0, color: "#F59E0B" },
          { label: "Taux de presence", value: `${s.attendance_rate || 0}%`, color: "#8B5CF6" },
        ].map((item, i) => (
          <div key={i} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/10">
            <p className="text-6xl font-bold mb-3" style={{ color: item.color }}>{item.value}</p>
            <p className="text-white/60 text-sm uppercase tracking-wider">{item.label}</p>
          </div>
        ))}
      </div>

      {/* Capacity bar */}
      {stats.event?.max_capacity > 0 && (
        <div className="mt-12 w-full max-w-3xl">
          <div className="flex justify-between text-sm text-white/50 mb-2">
            <span>Capacite</span>
            <span>{s.inscrits || 0} / {stats.event.max_capacity}</span>
          </div>
          <div className="h-3 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#0099CC] to-[#10B981] rounded-full transition-all duration-1000"
              style={{ width: `${Math.min(100, ((s.inscrits || 0) / stats.event.max_capacity) * 100)}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}


// Full-screen countdown for public display
function CountdownDisplay({ config }) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!config?.target_time) return;
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(config.target_time).getTime() - Date.now()) / 1000));
      setRemaining(diff);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [config?.target_time]);

  if (remaining === null) return <div className="w-full h-full bg-[#0B1120]" />;

  const done = remaining <= 0;
  const d = Math.floor(remaining / 86400);
  const h = Math.floor((remaining % 86400) / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;

  const segments = [];
  if (d > 0) segments.push({ val: String(d).padStart(2, "0"), label: "Jours" });
  segments.push({ val: String(h).padStart(2, "0"), label: "Heures" });
  segments.push({ val: String(m).padStart(2, "0"), label: "Minutes" });
  segments.push({ val: String(s).padStart(2, "0"), label: "Secondes" });

  return (
    <div className="w-full h-full bg-gradient-to-br from-[#0B1120] via-[#120a2e] to-[#1a0a2e] flex flex-col items-center justify-center p-12">
      {done ? (
        <div className="text-center animate-pulse">
          <p className="text-6xl font-bold text-green-400 mb-4">{config?.end_message || "C'est parti !"}</p>
        </div>
      ) : (
        <>
          <p className="text-white/60 text-2xl mb-8 font-light">{config?.message || "L'evenement commence dans"}</p>
          <div className="flex items-center gap-4">
            {segments.map((seg, i) => (
              <div key={i} className="flex items-center gap-4">
                {i > 0 && <span className="text-white/30 text-5xl font-light">:</span>}
                <div className="text-center">
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl px-6 py-5 border border-white/10 min-w-[110px]">
                    <p className="text-7xl font-bold text-white font-mono tabular-nums leading-none">{seg.val}</p>
                  </div>
                  <p className="text-white/40 text-sm mt-3 uppercase tracking-wider">{seg.label}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
